import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Pencil } from 'lucide-react';
import type { Theme } from './slideTypes';

const FEEDBACK_PRESETS = [
  'Not quite!',
  'Think again...',
  'Are you sure?',
  'Give it another go!',
  'Close, but not right.',
];

interface FeedbackSelectorProps {
  value: string;
  onChange: (value: string) => void;
  theme: Theme;
}

export default function FeedbackSelector({ value, onChange, theme }: FeedbackSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isPreset = FEEDBACK_PRESETS.includes(value);
  const isCustom = !!value && !isPreset;
  const [showCustomInput, setShowCustomInput] = useState(false);
  const customInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Focus custom input when it appears
  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [showCustomInput]);

  // Close on click outside
  useEffect(() => {
    if (!isExpanded) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsExpanded(false);
        setShowCustomInput(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isExpanded]);

  const handlePresetClick = (preset: string) => {
    onChange(preset);
    setIsExpanded(false);
    setShowCustomInput(false);
  };

  const handleCustomClick = () => {
    setShowCustomInput(true);
    if (!isPreset) {
      // Keep current custom value
    } else {
      onChange('');
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setIsExpanded(false);
    setShowCustomInput(false);
  };

  const handleCustomKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsExpanded(false);
      setShowCustomInput(false);
    }
  };

  // --- Ghost trigger (no feedback) ---
  if (!isExpanded && !value) {
    return (
      <motion.button
        onClick={() => setIsExpanded(true)}
        className="flex items-center gap-1.5 py-1 text-xs transition-colors rounded-md px-1 -ml-1"
        style={{ color: theme.colors.textMuted }}
        whileHover={{ color: theme.colors.primary }}
      >
        <MessageCircle className="w-3.5 h-3.5" />
        <span>Add feedback</span>
      </motion.button>
    );
  }

  // --- Filled pill (feedback set, collapsed) ---
  if (!isExpanded && value) {
    return (
      <motion.div
        className="flex items-center gap-1.5 mt-0.5"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.12 }}
      >
        <button
          onClick={() => {
            setIsExpanded(true);
            if (isCustom) setShowCustomInput(true);
          }}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-opacity hover:opacity-80 max-w-[280px]"
          style={{
            backgroundColor: `${theme.colors.primary}15`,
            color: theme.colors.primary,
          }}
        >
          <MessageCircle className="w-3 h-3 shrink-0" />
          <span className="truncate">{value}</span>
        </button>
        <button
          onClick={handleClear}
          className="p-0.5 rounded-full transition-colors hover:bg-red-50"
          style={{ color: theme.colors.textMuted }}
        >
          <X className="w-3 h-3" />
        </button>
      </motion.div>
    );
  }

  // --- Expanded (editing) ---
  return (
    <div ref={containerRef} className="mt-0.5">
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="overflow-hidden"
        >
          <div className="flex flex-wrap gap-1.5 py-1">
            {FEEDBACK_PRESETS.map((preset) => (
              <motion.button
                key={preset}
                onClick={() => handlePresetClick(preset)}
                className="px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
                style={{
                  backgroundColor: value === preset ? `${theme.colors.primary}15` : theme.colors.surface,
                  borderColor: value === preset ? theme.colors.primary : `${theme.colors.textMuted}30`,
                  color: value === preset ? theme.colors.primary : theme.colors.text,
                }}
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
              >
                {preset}
              </motion.button>
            ))}
            <motion.button
              onClick={handleCustomClick}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors"
              style={{
                backgroundColor: isCustom ? `${theme.colors.primary}15` : theme.colors.surface,
                borderColor: isCustom ? theme.colors.primary : `${theme.colors.textMuted}30`,
                color: isCustom ? theme.colors.primary : theme.colors.textMuted,
              }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              <Pencil className="w-3 h-3" />
              Custom...
            </motion.button>
          </div>

          <AnimatePresence>
            {showCustomInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.12 }}
                className="overflow-hidden"
              >
                <input
                  ref={customInputRef}
                  type="text"
                  value={isPreset ? '' : value}
                  onChange={(e) => onChange(e.target.value)}
                  onKeyDown={handleCustomKeyDown}
                  placeholder="Type your feedback..."
                  className="w-full mt-1 px-3 py-1.5 text-sm rounded-md border focus:outline-none focus:ring-1 transition-colors"
                  style={{
                    backgroundColor: theme.colors.surface,
                    borderColor: `${theme.colors.primary}40`,
                    color: theme.colors.text,
                  }}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
